import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Filter } from "lucide-react";
import { Features } from "@KassaroAB/shared/types/enums/Features";
import { TABLE_PRODUCT_TYPES } from "./productTableColumns";
import type {
  ProductPresenceFilter,
} from "../../../data/productStore.types";
import { PRODUCT_FILTER_NONE } from "../../../data/productStore.types";
import { borderRadius, spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import colors from "@styling/colors";
import { Dropdown } from "../../../components/Dropdown";
import { SelectInput } from "../../../components/SelectInput";
import { Button } from "../../../components/Button";
import { StyledInput } from "../../../components/Input";
import {
  formatMoneyAmountInputValue,
  isValidMoneyAmountInputDraft,
  moneyAmountInputToMinor,
} from "../../../utils/parseMoneyAmountInput";
import { useProductsTable } from "./productsTableContext";
import type { StockWarningFilter } from "./useProductTableFilters";

const VAT_OPTIONS = [25, 12, 6, 0] as const;
const UPDATED_WITHIN_DAYS_OPTIONS = [7, 30] as const;

const Fields = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${spacing.x1}px ${spacing.x2}px;
`;

const Field = styled.div<{ $wide?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x05}px;
  min-width: 0;
  ${({ $wide }) => $wide && "grid-column: 1 / -1;"}
`;

const FieldLabel = styled.span`
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
`;

const PriceRange = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: ${spacing.x1}px;
  color: ${colors.textMuted};
`;

const PriceInput = styled(StyledInput)`
  padding: ${spacing.x05 + 2}px ${spacing.x1}px;
  border-radius: ${borderRadius.small}px;
  background-color: ${colors.backgroundLight};
  font-size: ${fontSizes.medium}px;
  box-sizing: border-box;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${spacing.x2}px;
  padding-top: ${spacing.x2}px;
  border-top: 1px solid ${colors.border};
`;

const Label = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.x1}px;
`;

const PriceField = ({
  value,
  placeholder,
  onChange,
}: {
  value: number | null;
  placeholder: string;
  onChange: (amountMinor: number | null) => void;
}) => {
  const [draft, setDraft] = useState(formatMoneyAmountInputValue(value));
  const cleared = value === null && moneyAmountInputToMinor(draft) !== null;
  return (
    <PriceInput
      inputMode="decimal"
      placeholder={placeholder}
      value={cleared ? "" : draft}
      onChange={(event) => {
        const next = event.target.value;
        if (!isValidMoneyAmountInputDraft(next)) {
          return;
        }
        setDraft(next);
        onChange(next.trim() === "" ? null : moneyAmountInputToMinor(next));
      }}
    />
  );
};

const FilterSelect = ({
  label,
  value,
  onChange,
  wide,
  children,
}: {
  label: string;
  value: string;
  onChange: (value: string | null) => void;
  wide?: boolean;
  children: ReactNode;
}) => {
  const { t } = useTranslation();
  return (
    <Field $wide={wide}>
      <FieldLabel>{label}</FieldLabel>
      <SelectInput
        small
        value={value}
        onChange={(event) => onChange(event.target.value || null)}
      >
        <option value="">{t("productsView.filterAll")}</option>
        {children}
      </SelectInput>
    </Field>
  );
};

export const ProductFilterMenu = () => {
  const { t } = useTranslation();
  const {
    groups,
    hasFeature,
    bookkeepingAccounts,
    suppliers,
    units,
    showStockWarningFilter,
    stockLocationId,
    filters,
  } = useProductsTable();
  const activeCount = filters.activeFilterCount;

  return (
    <Dropdown
      small
      panelWidth={480}
      label={
        <Label>
          <Filter size={16} />
          {t("productsView.filters")}
          {activeCount > 0 ? ` (${activeCount})` : ""}
        </Label>
      }
    >
      <Fields>
        {showStockWarningFilter && (
          <FilterSelect
            label={t("productsView.filterStockWarning")}
            value={filters.stockWarningFilter ?? ""}
            onChange={(value) =>
              filters.setStockWarningFilter(value as StockWarningFilter | null)
            }
          >
            <option value="out">{t("productsView.filterStockWarningOut")}</option>
            <option value="low">{t("productsView.filterStockWarningLow")}</option>
          </FilterSelect>
        )}
        <FilterSelect
          label={t("productsView.filterVariants")}
          value={filters.variantsFilter ?? ""}
          onChange={(value) =>
            filters.setVariantsFilter(value as ProductPresenceFilter | null)
          }
        >
          <option value="has">{t("productsView.filterVariantsHas")}</option>
          <option value="none">{t("productsView.filterVariantsNone")}</option>
        </FilterSelect>
        {hasFeature([Features.GROUPS]) && (
          <FilterSelect
            label={t("productsView.tableGroup")}
            value={filters.groupId ?? ""}
            onChange={filters.setGroupId}
          >
            {groups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </FilterSelect>
        )}
        {hasFeature([Features.SUPPLIERS]) && (
          <FilterSelect
            label={t("productsView.filterSupplier")}
            value={filters.supplierFilter ?? ""}
            onChange={filters.setSupplierFilter}
          >
            <option value={PRODUCT_FILTER_NONE}>
              {t("productsView.filterNone")}
            </option>
            {suppliers.map((supplier) => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.name}
              </option>
            ))}
          </FilterSelect>
        )}
        <FilterSelect
          label={t("productsView.tableVat")}
          value={filters.vatFilter === null ? "" : String(filters.vatFilter)}
          onChange={(value) =>
            filters.setVatFilter(value === null ? null : Number(value))
          }
        >
          {VAT_OPTIONS.map((vat) => (
            <option key={vat} value={String(vat)}>
              {vat}%
            </option>
          ))}
        </FilterSelect>
        <FilterSelect
          label={t("productsView.tableType")}
          value={filters.typeFilter ?? ""}
          onChange={filters.setTypeFilter}
        >
          {TABLE_PRODUCT_TYPES.map((type) => (
            <option key={type} value={type}>
              {t(`productForm.type${type}`)}
            </option>
          ))}
        </FilterSelect>
        {units.length > 0 && (
          <FilterSelect
            label={t("productForm.unit")}
            value={filters.unitFilter ?? ""}
            onChange={filters.setUnitFilter}
          >
            <option value={PRODUCT_FILTER_NONE}>
              {t("productsView.filterNone")}
            </option>
            {units.map((unit) => (
              <option key={unit.id} value={unit.id}>
                {unit.name}
              </option>
            ))}
          </FilterSelect>
        )}
        <FilterSelect
          label={t("productForm.bookkeepingAccountId")}
          value={filters.bookkeepingAccountFilter ?? ""}
          onChange={filters.setBookkeepingAccountFilter}
        >
          <option value={PRODUCT_FILTER_NONE}>
            {t("productsView.filterBookkeepingDefault")}
          </option>
          {bookkeepingAccounts.map((account) => (
            <option key={account.id} value={account.id}>
              {account.accountNumber} {account.name}
            </option>
          ))}
        </FilterSelect>
        {hasFeature([Features.SIMPLE_STOCK]) &&
          stockLocationId === null && (
            <FilterSelect
              label={t("productsView.tableStock")}
              value={filters.stockFilter ?? ""}
              onChange={(value) =>
                filters.setStockFilter(value as ProductPresenceFilter | null)
              }
            >
              <option value="has">{t("productsView.filterStockHas")}</option>
              <option value="none">{t("productsView.filterStockNone")}</option>
            </FilterSelect>
          )}
        <FilterSelect
          label={t("productsView.filterUpdated")}
          value={
            filters.updatedWithinDays === null
              ? ""
              : String(filters.updatedWithinDays)
          }
          onChange={(value) =>
            filters.setUpdatedWithinDays(value === null ? null : Number(value))
          }
        >
          {UPDATED_WITHIN_DAYS_OPTIONS.map((days) => (
            <option key={days} value={String(days)}>
              {t("productsView.filterUpdatedWithin", { count: days })}
            </option>
          ))}
        </FilterSelect>
        <Field $wide>
          <FieldLabel>{t("productsView.filterPrice")}</FieldLabel>
          <PriceRange>
            <PriceField
              value={filters.priceMin}
              placeholder={t("productsView.filterPriceFrom")}
              onChange={filters.setPriceMin}
            />
            –
            <PriceField
              value={filters.priceMax}
              placeholder={t("productsView.filterPriceTo")}
              onChange={filters.setPriceMax}
            />
          </PriceRange>
        </Field>
      </Fields>
      {activeCount > 0 && (
        <Footer>
          <Button
            type="button"
            $small
            $color="secondary"
            $variant="outlined"
            onClick={filters.clearFilters}
          >
            {t("productsView.clearFilters")}
          </Button>
        </Footer>
      )}
    </Dropdown>
  );
};
